import React from "react"
import { ArrowLeft, Loader2 } from "lucide-react"

import { cn } from "@/lib/utils"
import { AuthLoginSubmit } from "@/components/auth-login-submit"
import {
  AuthMirroredSplitShell,
  AuthSplitSwitchLink,
} from "@/components/auth-mirrored-split-shell"
import { AuthOAuthButtons, type OAuthStrategy } from "@/components/auth-oauth-buttons"
import {
  AuthSwipePanel,
  AuthSwipeTrack,
  AuthSwipeViewport,
} from "@/components/auth-swipe-panels"
import {
  authSplitCardClass,
  authSplitCardShellClass,
  authSplitFormCenteredClass,
  authWelcomeTitleClass,
  AUTH_MUTED_TEXT_CLASS,
} from "@/components/auth-split-card-layout"
import { Card } from "@/components/ui/card"
import {
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  FieldSeparator,
} from "@/components/ui/field"
import { Input } from "@/components/ui/input"

export type LoginFormProps = {
  className?: string
  isResetMode: boolean
  resetStage: "request" | "verify"
  identifier: string
  password: string
  resetEmail: string
  resetCode: string
  resetNewPassword: string
  resetConfirmPassword: string
  showPassword: boolean
  showResetNewPassword: boolean
  showResetConfirmPassword: boolean
  loading: boolean
  resetLoading: boolean
  oauthLoading: OAuthStrategy | null
  error: string
  successMessage: string
  /** Demo Entry Analysis: sign in to unlock cooking report. */
  demoEntryAnalysisGate?: boolean
  onIdentifierChange: (value: string) => void
  onPasswordChange: (value: string) => void
  onResetEmailChange: (value: string) => void
  onResetCodeChange: (value: string) => void
  onResetNewPasswordChange: (value: string) => void
  onResetConfirmPasswordChange: (value: string) => void
  onShowPasswordToggle: () => void
  onShowResetNewPasswordToggle: () => void
  onShowResetConfirmPasswordToggle: () => void
  onSubmit: (e: React.FormEvent) => void
  onResetSubmit: (e: React.FormEvent) => void
  onStartReset: () => void
  onBackToSignIn: () => void
  onOAuthSignIn: (strategy: OAuthStrategy) => void
}

/** Tessera dashboard chrome radius (filters, cards, buttons). */
const AUTH_RADIUS_CLASS = "rounded-[6px]"

/** Urbanist — primary brand font for auth (headings, labels, body). */
const AUTH_FONT_PRIMARY = "font-[family-name:var(--font-primary)]"

/** Auth fields — Urbanist + theme border (radius from ui/input). */
const AUTH_INPUT_CLASS = cn(
  AUTH_FONT_PRIMARY,
  "h-8 text-sm !border-border text-foreground dark:!border-border dark:!text-white dark:placeholder:!text-white/50"
)

const AUTH_TROPICAL_SURF_TEXT = "text-[color:var(--brand-tropical-surf)]"

function FormAlert({
  variant,
  children,
}: {
  variant: "error" | "success"
  children: React.ReactNode
}) {
  return (
    <div
      data-auth-form-message
      role={variant === "error" ? "alert" : "status"}
      className={cn(
        "rounded-[6px] border px-3 py-2 text-sm",
        variant === "error"
          ? "border-destructive/40 bg-destructive/10 text-destructive"
          : "border-emerald-500/40 bg-emerald-500/10 text-emerald-800 dark:text-emerald-200"
      )}
    >
      {children}
    </div>
  )
}

export function LoginForm({
  className,
  isResetMode,
  resetStage,
  identifier,
  password,
  resetEmail,
  resetCode,
  resetNewPassword,
  resetConfirmPassword,
  loading,
  resetLoading,
  oauthLoading,
  error,
  successMessage,
  demoEntryAnalysisGate = false,
  onIdentifierChange,
  onPasswordChange,
  onResetEmailChange,
  onResetCodeChange,
  onResetNewPasswordChange,
  onResetConfirmPasswordChange,
  onSubmit,
  onResetSubmit,
  onStartReset,
  onBackToSignIn,
  onOAuthSignIn,
}: LoginFormProps) {
  const oauthBusy = oauthLoading !== null
  const signInBusy = loading || oauthBusy

  /* Both panels stay mounted for the swipe, so each keeps its own copy. */
  const resetDescription =
    resetStage === "request"
      ? "Enter your email and we will send a verification code"
      : "Enter the code from your email and choose a new password"

  return (
    <div
      data-auth-login-form
      className={authSplitCardShellClass(cn(AUTH_FONT_PRIMARY, className))}
    >
      <Card className={authSplitCardClass}>
        <AuthMirroredSplitShell variant="signin">
          <AuthSwipeViewport>
            <AuthSwipeTrack offset={isResetMode ? 1 : 0}>
              <AuthSwipePanel active={!isResetMode}>
                <form className={authSplitFormCenteredClass} onSubmit={onSubmit}>
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 data-auth-welcome-title className={authWelcomeTitleClass}>
                        {demoEntryAnalysisGate ? "Sign in to see your results" : "Welcome back"}
                      </h1>
                      <p className={cn("text-sm text-balance text-muted-foreground dark:!text-white", AUTH_FONT_PRIMARY)}>
                        {demoEntryAnalysisGate
                          ? "Your report is ready for review. Create a free account to unlock it."
                          : "Login with your Google account or email"}
                      </p>
                    </div>
                    {successMessage ? <FormAlert variant="success">{successMessage}</FormAlert> : null}
                    {error ? <FormAlert variant="error">{error}</FormAlert> : null}
                    <AuthOAuthButtons
                      action="Login"
                      oauthLoading={oauthLoading}
                      busy={signInBusy}
                      onOAuth={onOAuthSignIn}
                    />
                    <FieldSeparator className="*:data-[slot=field-separator-content]:bg-card dark:*:data-[slot=field-separator-content]:bg-sidebar">
                      Or continue with
                    </FieldSeparator>
                    <Field>
                      <FieldLabel
                        htmlFor="email"
                        data-auth-field-label
                        className={cn("font-medium", AUTH_FONT_PRIMARY, AUTH_TROPICAL_SURF_TEXT)}
                      >
                        Email
                      </FieldLabel>
                      <Input
                        id="email"
                        type="text"
                        value={identifier}
                        onChange={(e) => onIdentifierChange(e.target.value)}
                        placeholder="tes@example.com"
                        disabled={loading}
                        autoComplete="username"
                        required
                        className={AUTH_INPUT_CLASS}
                      />
                    </Field>
                    <Field className="[&_[data-auth-password-header]>*]:!w-auto">
                      <div
                        data-auth-password-header
                        className="flex w-full flex-row items-center justify-between gap-2"
                      >
                        <FieldLabel
                          htmlFor="password"
                          data-auth-field-label
                          className={cn(
                            "mb-0 w-auto shrink-0 font-medium",
                            AUTH_FONT_PRIMARY,
                            AUTH_TROPICAL_SURF_TEXT
                          )}
                        >
                          Password
                        </FieldLabel>
                        <button
                          type="button"
                          data-auth-forgot-password
                          className={cn(
                            "ml-auto w-auto shrink-0 text-sm underline-offset-4 hover:underline",
                            AUTH_MUTED_TEXT_CLASS,
                            AUTH_FONT_PRIMARY
                          )}
                          onClick={onStartReset}
                          disabled={loading}
                        >
                          Forgot your password?
                        </button>
                      </div>
                      <Input
                        id="password"
                        type="password"
                        value={password}
                        onChange={(e) => onPasswordChange(e.target.value)}
                        disabled={loading}
                        autoComplete="current-password"
                        required
                        className={AUTH_INPUT_CLASS}
                      />
                    </Field>
                    <Field className="gap-3">
                      <AuthLoginSubmit loading={loading}>
                        {loading ? (
                          <>
                            <Loader2 className="size-4 animate-spin" aria-hidden />
                            Signing in...
                          </>
                        ) : (
                          "Login"
                        )}
                      </AuthLoginSubmit>
                      <FieldDescription className="!mt-0 text-center nth-last-2:!mt-0 [&_a]:inline">
                        Don&apos;t have an account?{" "}
                        <AuthSplitSwitchLink
                          to="signup"
                          data-auth-text-link-signup
                          className={cn("inline hover:opacity-90", AUTH_TROPICAL_SURF_TEXT)}
                        >
                          Sign up
                        </AuthSplitSwitchLink>
                      </FieldDescription>
                    </Field>
                  </FieldGroup>
                </form>
              </AuthSwipePanel>

              <AuthSwipePanel active={isResetMode}>
                <form className={authSplitFormCenteredClass} onSubmit={onResetSubmit}>
                  <FieldGroup>
                    <div className="flex flex-col items-center gap-2 text-center">
                      <h1 data-auth-welcome-title className={authWelcomeTitleClass}>
                        Reset your password
                      </h1>
                      <p className={cn("text-sm text-balance text-muted-foreground dark:!text-white", AUTH_FONT_PRIMARY)}>
                        {resetDescription}
                      </p>
                    </div>
                    {successMessage ? <FormAlert variant="success">{successMessage}</FormAlert> : null}
                    {error ? <FormAlert variant="error">{error}</FormAlert> : null}
                    {resetStage === "request" ? (
                      <>
                        <Field>
                          <FieldLabel
                            htmlFor="reset-email"
                            data-auth-field-label
                            className={cn("font-medium", AUTH_FONT_PRIMARY, AUTH_TROPICAL_SURF_TEXT)}
                          >
                            Email
                          </FieldLabel>
                          <Input
                            id="reset-email"
                            type="email"
                            value={resetEmail}
                            onChange={(e) => onResetEmailChange(e.target.value)}
                            placeholder="tes@example.com"
                            disabled={resetLoading}
                            autoComplete="email"
                            required
                            className={AUTH_INPUT_CLASS}
                          />
                        </Field>
                        <Field className="gap-3">
                          <AuthLoginSubmit loading={resetLoading}>
                            {resetLoading ? (
                              <>
                                <Loader2 className="size-4 animate-spin" aria-hidden />
                                Sending code...
                              </>
                            ) : (
                              "Send reset code"
                            )}
                          </AuthLoginSubmit>
                        </Field>
                      </>
                    ) : (
                      <>
                        <Field>
                          <FieldLabel
                            htmlFor="reset-code"
                            data-auth-field-label
                            className={cn("font-medium", AUTH_FONT_PRIMARY, AUTH_TROPICAL_SURF_TEXT)}
                          >
                            Verification code
                          </FieldLabel>
                          <Input
                            id="reset-code"
                            type="text"
                            value={resetCode}
                            onChange={(e) => onResetCodeChange(e.target.value)}
                            placeholder="Enter the code from your email"
                            disabled={resetLoading}
                            autoComplete="one-time-code"
                            required
                            className={AUTH_INPUT_CLASS}
                          />
                        </Field>
                        <Field>
                          <FieldLabel
                            htmlFor="reset-new-password"
                            data-auth-field-label
                            className={cn("font-medium", AUTH_FONT_PRIMARY, AUTH_TROPICAL_SURF_TEXT)}
                          >
                            New password
                          </FieldLabel>
                          <Input
                            id="reset-new-password"
                            type="password"
                            value={resetNewPassword}
                            onChange={(e) => onResetNewPasswordChange(e.target.value)}
                            disabled={resetLoading}
                            autoComplete="new-password"
                            required
                            className={AUTH_INPUT_CLASS}
                          />
                        </Field>
                        <Field>
                          <FieldLabel
                            htmlFor="reset-confirm-password"
                            data-auth-field-label
                            className={cn("font-medium", AUTH_FONT_PRIMARY, AUTH_TROPICAL_SURF_TEXT)}
                          >
                            Confirm password
                          </FieldLabel>
                          <Input
                            id="reset-confirm-password"
                            type="password"
                            value={resetConfirmPassword}
                            onChange={(e) => onResetConfirmPasswordChange(e.target.value)}
                            disabled={resetLoading}
                            autoComplete="new-password"
                            required
                            className={AUTH_INPUT_CLASS}
                          />
                        </Field>
                        <Field className="gap-3">
                          <AuthLoginSubmit loading={resetLoading}>
                            {resetLoading ? (
                              <>
                                <Loader2 className="size-4 animate-spin" aria-hidden />
                                Resetting...
                              </>
                            ) : (
                              "Reset password"
                            )}
                          </AuthLoginSubmit>
                        </Field>
                      </>
                    )}
                    <Field>
                      <button
                        type="button"
                        data-auth-back-to-sign-in
                        className={cn(
                          "inline-flex w-fit items-center gap-1.5 text-sm underline-offset-4 hover:underline",
                          AUTH_MUTED_TEXT_CLASS,
                          AUTH_FONT_PRIMARY
                        )}
                        onClick={onBackToSignIn}
                        disabled={resetLoading}
                      >
                        <ArrowLeft className="size-4 shrink-0" aria-hidden />
                        Back to sign in
                      </button>
                    </Field>
                  </FieldGroup>
                </form>
              </AuthSwipePanel>
            </AuthSwipeTrack>
          </AuthSwipeViewport>
        </AuthMirroredSplitShell>
      </Card>
    </div>
  )
}
